import React, { useMemo, useState } from 'react';

import { Tag } from 'antd';
import { T, TF } from 'react-translator-component';
import styled from 'styled-components';

import { ApiEndpoint, internalGroups, isMutating, partnerEndpoints } from './apiCatalog';
import { INTEGRATION_SCOPES, isSensitiveScope, scopeHelp, scopeLabel } from './integrationLabels';
import { runEndpoint, TryItResult } from './tryIt';

/* -------------------------------------------------------------------------- */
/* Sistema de cor por método HTTP: a mesma cor no badge do endpoint em todo    */
/* lugar, para o método ser reconhecido de relance (verbo => ação).            */
/* -------------------------------------------------------------------------- */
type Verb = ApiEndpoint['method'];

const METHOD_COLORS: Record<Verb, { bg: string; fg: string }> = {
  GET: { bg: '#e7f1fb', fg: '#1a5c9c' },
  POST: { bg: '#e7f6ed', fg: '#1e7a45' },
  PUT: { bg: '#fdf1e3', fg: '#a6631a' },
  PATCH: { bg: '#fef7e0', fg: '#8a6d0b' },
  DELETE: { bg: '#fdeaea', fg: '#b23b3b' },
};

const methodColor = (verb: string) => METHOD_COLORS[verb as Verb] || METHOD_COLORS.GET;

/** Cor do antd Tag por tipo de autenticação, para diferenciar parceiro de interno. */
const authTagColor = (auth: ApiEndpoint['auth']): string | undefined => {
  switch (auth) {
    case 'INTEGRATION_TOKEN':
      return 'blue';
    case 'TENANT_ID':
      return 'geekblue';
    case 'PUBLIC':
      return 'green';
    default:
      return undefined;
  }
};

const authLabel = (auth: ApiEndpoint['auth']): string => {
  switch (auth) {
    case 'INTEGRATION_TOKEN':
      return 'Integration token';
    case 'TENANT_ID':
      return 'X-TENANT-ID';
    case 'SESSION':
      return 'Session';
    default:
      return 'Public';
  }
};

/* -------------------------------------------------------------------------- */
/* Layout: menu lateral fixo + conteúdo com rolagem própria.                   */
/* -------------------------------------------------------------------------- */
const Layout = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
`;

const Sidebar = styled.nav`
  position: sticky;
  top: 12px;
  flex: none;
  width: 224px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.21);
  padding: 8px;

  /* Em tela estreita o menu lateral atrapalha; some e o conteúdo ocupa tudo (há um seletor no topo). */
  @media (max-width: 900px) {
    display: none;
  }
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
  /* Só a documentação rola: o menu e o topo ficam parados. */
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  padding-right: 4px;
`;

const NavHeading = styled.div`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8d929e;
  padding: 14px 10px 4px;
`;

const NavItem = styled.button<{ active?: boolean }>`
  position: relative;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  align-items: center;
  width: 100%;
  text-align: left;
  border: none;
  background: ${({ active }) => (active ? '#eef4fd' : 'transparent')};
  padding: 7px 10px 7px 14px;
  border-radius: 4px;
  font-size: 13px;
  color: ${({ active, theme }) => (active ? theme.primary : '#12263f')};
  font-weight: ${({ active }) => (active ? 600 : 400)};
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;

  /* Barra de acento à esquerda no item ativo. */
  &::before {
    content: '';
    position: absolute;
    left: 4px;
    top: 6px;
    bottom: 6px;
    width: 3px;
    border-radius: 2px;
    background: ${({ active, theme }) => (active ? theme.primary : 'transparent')};
  }

  &:hover {
    background: ${({ active }) => (active ? '#eef4fd' : '#f3f6fb')};
    color: ${({ theme }) => theme.primary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.primary};
    outline-offset: -2px;
  }
`;

const NavCount = styled.span<{ active?: boolean }>`
  color: ${({ active, theme }) => (active ? theme.primary : '#8d929e')};
  background: ${({ active }) => (active ? '#dce9fb' : '#f0f2f5')};
  border-radius: 10px;
  padding: 0 8px;
  font-size: 11px;
  line-height: 18px;
  white-space: nowrap;
`;

/* Seletor de seção só para tela estreita (o menu lateral some abaixo de 900px). */
const MobileNav = styled.select`
  display: none;
  width: 100%;
  margin-bottom: 12px;
  padding: 8px 10px;
  border: 1px solid #d6d9de;
  border-radius: 4px;
  font-size: 13px;
  color: #12263f;
  background: #fff;

  @media (max-width: 900px) {
    display: block;
  }
`;

/* -------------------------------------------------------------------------- */
/* Seção de conteúdo.                                                          */
/* -------------------------------------------------------------------------- */
const Section = styled.section`
  background: #fff;
  border-radius: 4px;
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.21);
  padding: 0 24px 20px;
  margin-top: 4px;
`;

/* Cabeçalho da seção que fica fixo no topo enquanto os endpoints rolam por baixo. */
const SectionHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
  padding: 18px 0 12px;
  margin-bottom: 14px;
  border-bottom: 1px solid #eef0f3;
`;

const SectionHeadRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`;

const Title = styled.h3`
  font-size: 15px;
  color: #12263f;
  margin: 0;
`;

const CountBadge = styled.span`
  flex: none;
  color: #5b6472;
  background: #f0f2f5;
  border-radius: 12px;
  padding: 1px 10px;
  font-size: 11px;
`;

const Lead = styled.p`
  color: #5b6472;
  font-size: 13px;
  line-height: 1.6;
  margin: 8px 0 0;
  max-width: 820px;
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
`;

const SearchInput = styled.input`
  width: 100%;
  max-width: 320px;
  margin-top: 12px;
  border: 1px solid #d6d9de;
  border-radius: 4px;
  padding: 6px 10px;
  font-size: 12px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primary};
  }
`;

const StepTitle = styled.h4`
  font-size: 13px;
  color: #12263f;
  margin: 20px 0 6px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StepNumber = styled.span`
  background: ${({ theme }) => theme.primary};
  color: #fff;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  font-size: 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
`;

const Text = styled.p`
  color: #5b6472;
  font-size: 13px;
  line-height: 1.6;
  margin: 0 0 8px;
  max-width: 820px;
`;

const Code = styled.pre`
  background: #f6f8fa;
  border: 1px solid #e9ebee;
  border-radius: 4px;
  padding: 12px 14px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  color: #12263f;
  overflow-x: auto;
  margin: 0 0 12px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const Inline = styled.code`
  background: #f3f4f6;
  border-radius: 3px;
  padding: 1px 5px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
`;

const Empty = styled.p`
  color: #8d929e;
  font-size: 13px;
  padding: 24px 0;
  text-align: center;
`;

/* -------------------------------------------------------------------------- */
/* Cartão de endpoint.                                                         */
/* -------------------------------------------------------------------------- */
const EndpointBox = styled.div`
  border: 1px solid #e9ebee;
  border-radius: 4px;
  padding: 12px 14px;
  margin-bottom: 10px;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;

  &:hover {
    border-color: #d6dbe2;
    box-shadow: 0 1px 4px rgba(18, 38, 63, 0.06);
  }
`;

const EndpointHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Method = styled.span<{ verb: string }>`
  background: ${({ verb }) => methodColor(verb).bg};
  color: ${({ verb }) => methodColor(verb).fg};
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  flex: none;
`;

const Path = styled.code`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12.5px;
  color: #12263f;
  word-break: break-all;
  margin-right: auto;
`;

const Summary = styled.p`
  color: #5b6472;
  font-size: 12px;
  line-height: 1.5;
  margin: 8px 0 0;
`;

const FieldGroupTitle = styled.p`
  color: #8d929e;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 12px 0 4px;
`;

const ParamTable = styled.table`
  width: 100%;
  margin-top: 8px;
  font-size: 12px;
  border-collapse: collapse;

  th,
  td {
    text-align: left;
    padding: 6px 8px;
    border-bottom: 1px solid #f3f4f6;
    color: #5b6472;
    vertical-align: top;
  }

  thead th {
    color: #8d929e;
    font-weight: 500;
    background: #fafbfc;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const WhereTag = styled.span<{ where: string }>`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-radius: 3px;
  padding: 1px 6px;
  color: ${({ where }) => (where === 'path' ? '#8a6d0b' : where === 'header' ? '#1a5c9c' : '#1e7a45')};
  background: ${({ where }) => (where === 'path' ? '#fef7e0' : where === 'header' ? '#e7f1fb' : '#e7f6ed')};
`;

const ScopeRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  border-bottom: 1px solid #f3f4f6;
  padding: 12px 0;

  &:last-of-type {
    border-bottom: none;
  }
`;

const ScopeName = styled.div`
  min-width: 170px;
  font-size: 13px;
  color: #12263f;
`;

const ScopeText = styled.div`
  color: #5b6472;
  font-size: 12px;
  line-height: 1.5;
`;

/* -------------------------------------------------------------------------- */
/* Try it.                                                                     */
/* -------------------------------------------------------------------------- */
const TryToggle = styled.button`
  margin-top: 12px;
  background: none;
  border: 1px solid #d6d9de;
  border-radius: 4px;
  color: #12263f;
  font-size: 12px;
  font-weight: 500;
  padding: 5px 12px;
  cursor: pointer;
  transition: border-color 0.12s ease, color 0.12s ease;

  &:hover {
    border-color: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.primary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.primary};
    outline-offset: 1px;
  }
`;

const TryPanel = styled.div`
  margin-top: 12px;
  border-top: 1px dashed #e2e5ea;
  padding-top: 12px;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 10px 14px;
`;

const Field = styled.label`
  display: block;
  font-size: 12px;
  color: #5b6472;
`;

const FieldLabel = styled.span`
  display: block;
  margin-bottom: 3px;
  color: #8d929e;
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid #d6d9de;
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  border: 1px solid #d6d9de;
  border-radius: 4px;
  padding: 8px;
  font-size: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  min-height: 120px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primary};
  }
`;

const SendButton = styled.button`
  margin-top: 12px;
  background: ${({ theme }) => theme.primary};
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  padding: 7px 16px;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.primary};
    outline-offset: 2px;
  }
`;

const StatusLine = styled.div<{ ok: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  margin: 12px 0 6px;
  padding: 3px 10px;
  border-radius: 12px;
  color: ${({ ok }) => (ok ? '#1e7a45' : '#b23b3b')};
  background: ${({ ok }) => (ok ? '#e7f6ed' : '#fdeaea')};
`;

const ResponseBox = styled(Code)`
  max-height: 320px;
  overflow: auto;
  white-space: pre;
  margin-bottom: 0;
`;

const ParamRows: React.FC<{ endpoint: ApiEndpoint }> = ({ endpoint }) => {
  const rows = [
    ...(endpoint.pathParams ?? []).map((p) => ({ ...p, where: 'path' })),
    ...(endpoint.headers ?? []).map((p) => ({ ...p, where: 'header' })),
    ...(endpoint.query ?? []).map((p) => ({ ...p, where: 'query' })),
  ];

  if (!rows.length) return null;

  return (
    <>
      <FieldGroupTitle>{T('Parameters')}</FieldGroupTitle>
      <ParamTable>
        <thead>
          <tr>
            <th>{T('Parameter')}</th>
            <th>{T('In')}</th>
            <th>{T('Description')}</th>
            <th>{T('Example')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((param) => (
            <tr key={`${param.where}:${param.name}`}>
              <td>
                <Inline>{param.name}</Inline>
              </td>
              <td>
                <WhereTag where={param.where}>{param.where}</WhereTag>
              </td>
              <td>{param.description || '—'}</td>
              <td>{param.example || '—'}</td>
            </tr>
          ))}
        </tbody>
      </ParamTable>
    </>
  );
};

const EndpointCard: React.FC<{ endpoint: ApiEndpoint; baseUrl: string }> = ({ endpoint, baseUrl }) => {
  const [open, setOpen] = useState(false);
  const [pathValues, setPathValues] = useState<Record<string, string>>({});
  const [queryValues, setQueryValues] = useState<Record<string, string>>({});
  const [tenantId, setTenantId] = useState('');
  const [integrationToken, setIntegrationToken] = useState('');
  const [body, setBody] = useState(endpoint.bodyExample || '');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<TryItResult | null>(null);
  const [error, setError] = useState('');

  const send = async () => {
    if (isMutating(endpoint)) {
      // Método que altera estado: dispara operação real neste ambiente (inicia/para recarga,
      // cria/remove vínculo). Confirma antes.
      const ok = window.confirm(
        `${endpoint.method} ${endpoint.path}\n\n` +
          'Isto executa uma operação real neste ambiente. Continuar?',
      );
      if (!ok) return;
    }

    setRunning(true);
    setError('');
    setResult(null);
    try {
      const res = await runEndpoint(baseUrl, endpoint, {
        pathValues,
        queryValues,
        tenantId,
        integrationToken,
        body,
      });
      setResult(res);
    } catch (e) {
      setError((e as Error).message || 'Falha na requisição');
    } finally {
      setRunning(false);
    }
  };

  return (
    <EndpointBox>
      <EndpointHead>
        <Method verb={endpoint.method}>{endpoint.method}</Method>
        <Path>{endpoint.path}</Path>
        <Tag color={authTagColor(endpoint.auth)}>{authLabel(endpoint.auth)}</Tag>
        {(endpoint.scopes ?? []).map((scope) => (
          <Tag key={scope} color={isSensitiveScope(scope) ? 'orange' : undefined}>
            {scopeLabel(scope)}
          </Tag>
        ))}
      </EndpointHead>

      <Summary>{endpoint.summary}</Summary>

      <ParamRows endpoint={endpoint} />

      {endpoint.bodyExample && (
        <>
          <FieldGroupTitle>{TF('Request body')}</FieldGroupTitle>
          <Code>{endpoint.bodyExample}</Code>
        </>
      )}

      {endpoint.responseExample && (
        <>
          <FieldGroupTitle>{TF('Response')}</FieldGroupTitle>
          <Code>{endpoint.responseExample}</Code>
        </>
      )}

      <TryToggle type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {open ? T('Hide Try it') : T('Try it')}
      </TryToggle>

      {open && (
        <TryPanel>
          {endpoint.auth === 'INTEGRATION_TOKEN' && (
            <Field>
              <FieldLabel>{T('Integration token (Bearer) — not stored')}</FieldLabel>
              <Input
                value={integrationToken}
                onChange={(e) => setIntegrationToken(e.target.value)}
                placeholder="move_key_…"
              />
            </Field>
          )}

          {endpoint.auth === 'TENANT_ID' && (
            <Field>
              <FieldLabel>X-TENANT-ID {T('(defaults to the current tenant)')}</FieldLabel>
              <Input
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                placeholder={T('current tenant')}
              />
            </Field>
          )}

          {(endpoint.pathParams?.length || endpoint.query?.length) ? (
            <FieldGrid>
              {(endpoint.pathParams ?? []).map((param) => (
                <Field key={`path:${param.name}`}>
                  <FieldLabel>
                    {param.name} <em>({T('path')})</em>
                  </FieldLabel>
                  <Input
                    value={pathValues[param.name] || ''}
                    placeholder={param.example || ''}
                    onChange={(e) => setPathValues((v) => ({ ...v, [param.name]: e.target.value }))}
                  />
                </Field>
              ))}

              {(endpoint.query ?? []).map((param) => (
                <Field key={`query:${param.name}`}>
                  <FieldLabel>
                    {param.name} <em>({T('query')})</em>
                  </FieldLabel>
                  <Input
                    value={queryValues[param.name] || ''}
                    placeholder={param.example || ''}
                    onChange={(e) => setQueryValues((v) => ({ ...v, [param.name]: e.target.value }))}
                  />
                </Field>
              ))}
            </FieldGrid>
          ) : null}

          {endpoint.bodyExample && endpoint.method !== 'GET' && (
            <Field style={{ marginTop: 10 }}>
              <FieldLabel>{T('Body')}</FieldLabel>
              <TextArea value={body} onChange={(e) => setBody(e.target.value)} />
            </Field>
          )}

          <div>
            <SendButton type="button" onClick={send} disabled={running}>
              {running ? T('Sending…') : `${T('Send')} ${endpoint.method}`}
            </SendButton>
          </div>

          {error && <StatusLine ok={false}>{error}</StatusLine>}

          {result && (
            <div>
              <StatusLine ok={result.ok}>
                {`HTTP ${result.status} · ${result.durationMs} ms`}
              </StatusLine>
              <ResponseBox>{result.body || T('(empty response)')}</ResponseBox>
            </div>
          )}
        </TryPanel>
      )}
    </EndpointBox>
  );
};

/** Uma seção com lista de endpoints (parceiro ou grupo interno), com busca e legenda de auth. */
const EndpointSection: React.FC<{
  title: string;
  lead: React.ReactNode;
  endpoints: ApiEndpoint[];
  baseUrl: string;
}> = ({ title, lead, endpoints, baseUrl }) => {
  const [term, setTerm] = useState('');

  const auths = useMemo(
    () => Array.from(new Set(endpoints.map((endpoint) => endpoint.auth))),
    [endpoints],
  );

  const filtered = useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (!needle) return endpoints;
    return endpoints.filter((endpoint) =>
      `${endpoint.method} ${endpoint.path} ${endpoint.summary}`.toLowerCase().includes(needle),
    );
  }, [endpoints, term]);

  return (
    <Section>
      <SectionHeader>
        <SectionHeadRow>
          <Title>{title}</Title>
          <CountBadge>
            {endpoints.length} {TF('endpoints')}
          </CountBadge>
        </SectionHeadRow>
        <Lead>{lead}</Lead>
        <Legend>
          {auths.map((auth) => (
            <Tag key={auth} color={authTagColor(auth)}>
              {authLabel(auth)}
            </Tag>
          ))}
        </Legend>
        <SearchInput
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder={TF('Filter by method, path or description')}
          aria-label={TF('Filter endpoints')}
        />
      </SectionHeader>

      {filtered.length === 0 ? (
        <Empty>{T('No endpoint matches this search.')}</Empty>
      ) : (
        filtered.map((endpoint) => (
          <EndpointCard key={`${endpoint.method} ${endpoint.path}`} endpoint={endpoint} baseUrl={baseUrl} />
        ))
      )}
    </Section>
  );
};

export type ApiDocumentationProps = {
  /** Endereço deste ambiente, o mesmo que a coleção do Postman leva. */
  baseUrl: string;
};

/**
 * Documentação da API do tenant-cross.
 *
 * Menu lateral fixo à esquerda; o conteúdo (uma seção por vez) rola sozinho. Duas famílias: a
 * API de parceiro (/integrations, token de integração, escopo) e a API interna (gerada do
 * OpenAPI do backend). O "Try it" dispara a requisição real com a autenticação certa de cada
 * uma; método que altera estado confirma antes.
 */
export const ApiDocumentation: React.FC<ApiDocumentationProps> = ({ baseUrl }) => {
  const partners = partnerEndpoints();
  const groups = internalGroups();
  const partnerTotal = partners.length;
  const [selected, setSelected] = useState<string>('howto');

  const howTo = (
    <Section>
      <SectionHeader>
        <SectionHeadRow>
          <Title>{T('How to integrate')}</Title>
        </SectionHeadRow>
        <Lead>
          {T(
            'This API exposes what this account shares through interoperability contracts. An integrator authenticates with a token you create here, and sees only what the token scopes allow.',
          )}
        </Lead>
      </SectionHeader>

      <StepTitle>
        <StepNumber>1</StepNumber>
        {T('Create a token with the scopes the integrator needs')}
      </StepTitle>
      <Text>
        {T(
          'Use "New token" above. Nothing is granted by default — mark only what they need. The value appears once, right after creation, and is never shown again.',
        )}
      </Text>

      <StepTitle>
        <StepNumber>2</StepNumber>
        {T('Send the token on every request')}
      </StepTitle>
      <Text>
        {T('The token goes in the')} <Inline>Authorization</Inline>{' '}
        {T('header, never in the URL — a URL ends up in browser history and proxy logs.')}
      </Text>
      <Code>{`curl "${baseUrl}/integrations/connections" \\
  -H "Authorization: Bearer move_key_…" \\
  -H "Accept: application/json"`}</Code>
      <Text>
        {T('The base address of this environment is')} <Inline>{baseUrl}</Inline>.{' '}
        {T('Note there is no tenant in the path: the token itself says which account it is.')}
      </Text>

      <StepTitle>
        <StepNumber>3</StepNumber>
        {T('Handle the refusals')}
      </StepTitle>
      <Text>
        <Inline>401</Inline> {T('means the token is missing, revoked or expired.')}{' '}
        <Inline>403</Inline>{' '}
        {T(
          'means the token is valid but lacks a scope this endpoint requires — ask for a token with that scope instead of retrying.',
        )}{' '}
        <Inline>400</Inline> {T('means something in the request is wrong.')}
      </Text>
    </Section>
  );

  const scopesView = (
    <Section>
      <SectionHeader>
        <SectionHeadRow>
          <Title>{T('Scopes')}</Title>
        </SectionHeadRow>
        <Lead>{T('What each scope unlocks. They are independent: one does not imply another.')}</Lead>
      </SectionHeader>

      {INTEGRATION_SCOPES.map((scope) => (
        <ScopeRow key={scope}>
          <ScopeName>
            <Tag color={isSensitiveScope(scope) ? 'orange' : undefined}>{scopeLabel(scope)}</Tag>
          </ScopeName>
          <ScopeText>{scopeHelp(scope)}</ScopeText>
        </ScopeRow>
      ))}
    </Section>
  );

  const partnerView = (
    <EndpointSection
      key="partner"
      title={T('Partner API')}
      lead={
        <>
          {T('The integration endpoints, authenticated by the integration token.')}{' '}
          {T('The same list is in the Postman collection you can download above.')}
        </>
      }
      endpoints={partners}
      baseUrl={baseUrl}
    />
  );

  const groupView = (group: { group: string; endpoints: ApiEndpoint[] }) => (
    <EndpointSection
      key={`group:${group.group}`}
      title={group.group}
      lead={T(
        'Internal tenant-cross endpoints, generated from the backend. They authenticate by tenant identity or session, not by the integration token.',
      )}
      endpoints={group.endpoints}
      baseUrl={baseUrl}
    />
  );

  const selectedGroup = groups.find((group) => `group:${group.group}` === selected);
  let current = howTo;
  if (selected === 'scopes') current = scopesView;
  else if (selected === 'partner') current = partnerView;
  else if (selectedGroup) current = groupView(selectedGroup);

  return (
    <Layout>
      <Sidebar aria-label={TF('API documentation sections')}>
        <NavHeading>{T('Guide')}</NavHeading>
        <NavItem
          type="button"
          aria-current={selected === 'howto'}
          active={selected === 'howto'}
          onClick={() => setSelected('howto')}
        >
          {T('How to integrate')}
        </NavItem>
        <NavItem
          type="button"
          aria-current={selected === 'scopes'}
          active={selected === 'scopes'}
          onClick={() => setSelected('scopes')}
        >
          {T('Scopes')}
        </NavItem>

        <NavHeading>{T('Partner API')}</NavHeading>
        <NavItem
          type="button"
          aria-current={selected === 'partner'}
          active={selected === 'partner'}
          onClick={() => setSelected('partner')}
        >
          {T('Endpoints')}
          <NavCount active={selected === 'partner'}>{partnerTotal}</NavCount>
        </NavItem>

        <NavHeading>{T('Internal API')}</NavHeading>
        {groups.map((group) => {
          const key = `group:${group.group}`;
          return (
            <NavItem
              key={group.group}
              type="button"
              aria-current={selected === key}
              active={selected === key}
              onClick={() => setSelected(key)}
            >
              {group.group}
              <NavCount active={selected === key}>{group.endpoints.length}</NavCount>
            </NavItem>
          );
        })}
      </Sidebar>

      <Content>
        <MobileNav
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          aria-label={TF('API documentation sections')}
        >
          <option value="howto">{TF('How to integrate')}</option>
          <option value="scopes">{TF('Scopes')}</option>
          <option value="partner">
            {TF('Partner API')} ({partnerTotal})
          </option>
          {groups.map((group) => (
            <option key={group.group} value={`group:${group.group}`}>
              {group.group} ({group.endpoints.length})
            </option>
          ))}
        </MobileNav>

        {current}
      </Content>
    </Layout>
  );
};
